import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { patientFileService } from '../services/patientFileService'
import { labResultService } from '../services/labResultService'
import { PatientFile } from '../types/patientFile'
import { PatientFileViewer } from './PatientFileViewer'
import { MaskedField } from './MaskedField'
import { formatDate } from '../utils/locale'
import './PatientFileList.css'

interface PatientFileListProps {
  patientId: string
}

export function PatientFileList({ patientId }: PatientFileListProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [selectedSource, setSelectedSource] = useState<string>('all')
  const [viewingFile, setViewingFile] = useState<PatientFile | null>(null)

  // Fetch patient files
  const { data: patientFiles, isLoading: filesLoading } = useQuery({
    queryKey: ['patient-files', patientId],
    queryFn: () => patientFileService.getByPatient(patientId),
  })

  // Fetch lab results to get their attachments
  const { data: labResults, isLoading: labResultsLoading } = useQuery({
    queryKey: ['lab-results', 'patient', patientId],
    queryFn: () => labResultService.getByPatient(patientId),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => patientFileService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-files', patientId] })
    },
  })

  // Combine patient files with lab result attachments
  const allFiles: Array<PatientFile & { labResultId?: string; labResultDate?: string }> = []

  // Add direct patient files
  patientFiles?.forEach((file) => {
    allFiles.push(file)
  })

  // Add lab result attachments
  labResults?.forEach((labResult) => {
    labResult.attachments?.forEach((attachment) => {
      allFiles.push({
        id: attachment.id,
        patientId,
        fileName: attachment.fileName,
        fileType: attachment.fileType,
        fileSize: attachment.fileSize,
        // fileUrl removed for security - use download endpoint instead
        source: 'lab-result',
        sourceId: labResult.id,
        sourceType: 'Lab Result',
        uploadedAt: attachment.uploadedAt,
        createdAt: attachment.uploadedAt,
        updatedAt: attachment.uploadedAt,
        labResultId: labResult.id,
        labResultDate: labResult.reportedDate,
      })
    })
  })

  // Filter files
  const filteredFiles = allFiles.filter((file) => {
    if (selectedCategory !== 'all' && file.category !== selectedCategory) return false
    if (selectedSource !== 'all' && file.source !== selectedSource) return false
    return true
  })

  // Sort by date (newest first)
  const sortedFiles = [...filteredFiles].sort((a, b) => {
    return new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
  })

  const handleDelete = async (file: PatientFile) => {
    if (file.source === 'lab-result') {
      alert('Files from lab results cannot be deleted from here. Please manage them from the lab result detail page.')
      return
    }

    if (window.confirm(`Are you sure you want to delete "${file.fileName}"?`)) {
      try {
        await deleteMutation.mutateAsync(file.id)
      } catch (error: any) {
        alert(error?.message || 'Failed to delete file')
      }
    }
  }

  const handleDownload = async (file: PatientFile) => {
    try {
      // Always use secure download endpoint for PHI security
      // Never use presigned URLs directly
      const blob = await patientFileService.download(file.id)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = file.fileName
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error: any) {
      alert(error?.message || 'Failed to download file')
    }
  }

  const getFileIcon = (fileType: string): string => {
    if (fileType.startsWith('image/')) return '🖼️'
    if (fileType.includes('pdf')) return '📄'
    if (fileType.includes('word') || fileType.includes('document')) return '📝'
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊'
    if (fileType.includes('zip') || fileType.includes('archive')) return '📦'
    return '📎'
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const getCategoryLabel = (category?: string): string => {
    const labels: Record<string, string> = {
      'medical-record': 'Medical Record',
      insurance: 'Insurance',
      id: 'ID Document',
      referral: 'Referral',
      correspondence: 'Correspondence',
      other: 'Other',
    }
    return labels[category || 'other'] || 'Other'
  }

  const getSourceLabel = (source: string): string => {
    const labels: Record<string, string> = {
      upload: 'Uploaded',
      'lab-result': 'Lab Result',
      consultation: 'Consultation',
      import: 'Imported',
      other: 'Other',
    }
    return labels[source] || source
  }

  const isViewable = (fileType: string): boolean => {
    return (
      fileType.startsWith('image/') ||
      fileType === 'application/pdf' ||
      fileType.startsWith('text/') ||
      fileType === 'application/json' ||
      fileType === 'application/xml'
    )
  }

  const handleView = (file: PatientFile) => {
    setViewingFile(file)
  }

  // Helper function to render file item (extracted for reuse)
  function renderFileItem(file: typeof sortedFiles[0]) {
    return (
      <>
        <div className="file-item-icon">{getFileIcon(file.fileType)}</div>
        <div className="file-item-content">
          <div className="file-item-header">
            <h4 className="file-item-name">{file.displayName || file.fileName}</h4>
            <div className="file-item-badges">
              {file.category && (
                <span className="badge badge-category">{getCategoryLabel(file.category)}</span>
              )}
              <span className="badge badge-source">{getSourceLabel(file.source)}</span>
            </div>
          </div>
          <div className="file-item-meta">
            <span className="file-meta-item">{formatFileSize(file.fileSize)}</span>
            <span className="file-meta-item">•</span>
            <span className="file-meta-item">{formatDate(file.uploadedAt)}</span>
            {file.uploadedByName && (
              <>
                <span className="file-meta-item">•</span>
                <span className="file-meta-item">by {file.uploadedByName}</span>
              </>
            )}
            {file.source === 'lab-result' && file.labResultId && (
              <>
                <span className="file-meta-item">•</span>
                <button
                  className="link-button"
                  onClick={() => navigate(`/lab-results/${file.labResultId}`)}
                >
                  View Lab Result →
                </button>
              </>
            )}
          </div>
          {file.description && (
            <p className="file-item-description">{file.description}</p>
          )}
        </div>
        <div className="file-item-actions">
          {isViewable(file.fileType) && (
            <button
              className="btn-icon"
              onClick={() => handleView(file)}
              title="View"
            >
              👁️
            </button>
          )}
          <button
            className="btn-icon"
            onClick={() => handleDownload(file)}
            title="Download"
          >
            ⬇️
          </button>
          {file.source !== 'lab-result' && (
            <button
              className="btn-icon btn-icon-danger"
              onClick={() => handleDelete(file)}
              title="Delete"
              disabled={deleteMutation.isPending}
            >
              🗑️
            </button>
          )}
        </div>
      </>
    )
  }

  if (filesLoading || labResultsLoading) {
    return <div className="patient-file-list-loading">Loading files...</div>
  }

  if (sortedFiles.length === 0) {
    return (
      <div className="patient-file-list-empty">
        <p>No files found for this patient.</p>
        <p className="hint">Upload files or check lab results for attachments.</p>
      </div>
    )
  }

  return (
    <div className="patient-file-list">
      <div className="file-list-filters">
        <div className="filter-group">
          <label htmlFor="category-filter">Category</label>
          <select
            id="category-filter"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="form-control"
          >
            <option value="all">All Categories</option>
            <option value="medical-record">Medical Record</option>
            <option value="insurance">Insurance</option>
            <option value="id">ID Document</option>
            <option value="referral">Referral</option>
            <option value="correspondence">Correspondence</option>
            <option value="other">Other</option>
            <option value="sensitive">Sensitive</option>
            <option value="confidential">Confidential</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="source-filter">Source</label>
          <select
            id="source-filter"
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
            className="form-control"
          >
            <option value="all">All Sources</option>
            <option value="upload">Uploaded</option>
            <option value="lab-result">Lab Result</option>
            <option value="consultation">Consultation</option>
            <option value="import">Imported</option>
          </select>
        </div>
      </div>

      {filesLoading || labResultsLoading ? (
        <div className="file-list-loading">Loading files...</div>
      ) : filteredFiles.length === 0 ? (
        <div className="file-list-empty">No files found.</div>
      ) : (
        <div className="file-list">
          {sortedFiles.map((file) => {
            // Check if file is sensitive
            const isSensitive = file.category === 'sensitive' || 
                              file.category === 'confidential'

            // For sensitive files, wrap with MaskedField
            if (isSensitive) {
              return (
                <MaskedField
                  key={file.id}
                  field="sensitive-documents"
                  value={file}
                  patientId={patientId}
                  confidentialityLevel="confidential"
                >
                  {(canAccess, _maskedFile) => {
                    if (!canAccess) {
                      return (
                        <div key={file.id} className="file-item file-item-restricted">
                          <div className="file-item-icon">{getFileIcon(file.fileType)}</div>
                          <div className="file-item-content">
                            <div className="file-item-header">
                              <h4 className="file-item-name">Restricted Document</h4>
                              <div className="file-item-badges">
                                <span className="badge badge-restricted">Restricted</span>
                              </div>
                            </div>
                            <div className="file-item-meta">
                              <span className="file-meta-item">Access restricted</span>
                            </div>
                          </div>
                        </div>
                      )
                    }

                    // User has access - render full file details
                    return (
                      <div key={file.id} className="file-item">
                        {renderFileItem(file)}
                      </div>
                    )
                  }}
                </MaskedField>
              )
            }

            // Non-sensitive files - render normally
            return (
              <div key={file.id} className="file-item">
                {renderFileItem(file)}
              </div>
            )
          })}
        </div>
      )}

      {viewingFile && (
        <PatientFileViewer
          file={viewingFile}
          onClose={() => setViewingFile(null)}
        />
      )}
    </div>
  )
}






























